import React, { useState } from 'react';
import Select from 'react-select';

import RequiredProxyInput from 'components/elements/RequiredProxyInput';

export interface Option {
  value: number;
  label: string;
}

interface MultiSelectProps {
  name: string;
  options: Option[];
  defaultValue?: Option[];
  // Modo controlado (opcional): si se pasan `value` + `onChange`, el estado lo maneja el padre.
  value?: Option[];
  onChange?: (selected: Option[]) => void;
  placeholder?: string;
  className?: string;
  // PLA-1099: sólo lectura (p. ej. un contrato a precio fijo ya firmado). Los `<input hidden>`
  // se siguen emitiendo, porque viven fuera del <Select>; quien decide qué se acepta es el
  // strong param del controller.
  isDisabled?: boolean;
  // PLA-1099: permite vetar opciones según lo ya elegido (un contrato no puede mezclar modos
  // de liquidación). Es UX: la garantía real es la validación del modelo.
  isOptionDisabled?: (option: Option) => boolean;
  // PLA-1099: obligatorio. `react-select` 5.2.1 ignora su propio prop `required`, así que la
  // validación del navegador se consigue con RequiredProxyInput.
  required?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  name,
  options,
  defaultValue = [],
  value,
  onChange,
  placeholder = 'Seleccione opciones...',
  className = '',
  isDisabled = false,
  isOptionDisabled,
  required = false,
}) => {
  const [internalSelected, setInternalSelected] = useState<Option[]>(defaultValue);
  const isControlled = value !== undefined;
  const selectedOptions = isControlled ? (value as Option[]) : internalSelected;

  const handleChange = (selected: any) => {
    const newSelection = selected || [];
    if (!isControlled) setInternalSelected(newSelection);
    onChange?.(newSelection);
  };

  const customStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '38px',
      borderColor: '#ccc',
      '&:hover': {
        borderColor: '#999',
      },
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: '#e3f2fd',
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: '#1976d2',
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: '#1976d2',
      '&:hover': {
        backgroundColor: '#bbdefb',
        color: '#0d47a1',
      },
    }),
  };

  return (
    // `position: relative` ancla el testigo de RequiredProxyInput, que va en posición absoluta
    // para no ocupar espacio. Sin offsets, `relative` no mueve nada del layout actual.
    <div className={className} style={{ position: 'relative' }}>
      <Select
        isMulti
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isOptionDisabled={isOptionDisabled}
        styles={customStyles}
        className='react-select-container'
        classNamePrefix='react-select'
      />
      {selectedOptions.map((option) => (
        <input key={option.value} type='hidden' name={`${name}[]`} value={option.value} />
      ))}

      {required && (
        <RequiredProxyInput filled={selectedOptions.length > 0} disabled={isDisabled} />
      )}
    </div>
  );
};

export default MultiSelect;
